import { useEffect, useMemo, useRef } from "react";
import type { ExpectedHit } from "../../lib/rhythm/rhythmEngine";
import { TIMELINE_PX_PER_BEAT } from "../../lib/rhythm/musicGlyphs";
import { StringNumberBadge } from "./StringNumberBadge";
import { RhythmFigureMini } from "./RhythmFigureGlyph";
import { STRING_BADGE_COLOR } from "./StringNumberBadge";

export interface RhythmExerciseTimelineProps {
  expected: ExpectedHit[];
  /** Índice del evento activo (−1 si ninguno). */
  activeIndex: number;
  className?: string;
  /** Modo rasgueo: flechas ↑↓ en lugar de número de cuerda. */
  strumMode?: boolean;
}

type MeasureCell = {
  hit: ExpectedHit;
  globalIndex: number;
};

type Measure = {
  index: number;
  cells: MeasureCell[];
  /** Suma de beats ocupados (idealmente 4). */
  beats: number;
};

function buildMeasures(expected: ExpectedHit[]): Measure[] {
  const map = new Map<number, Measure>();
  for (const hit of expected) {
    const mIdx = Math.floor(hit.beatOffset / 4 + 1e-9);
    let m = map.get(mIdx);
    if (!m) {
      m = { index: mIdx, cells: [], beats: 0 };
      map.set(mIdx, m);
    }
    m.cells.push({ hit, globalIndex: hit.index });
    m.beats += hit.durationBeats;
  }
  return [...map.values()].sort((a, b) => a.index - b.index);
}

/**
 * Timeline profesional: cada figura ocupa un ancho proporcional a su duración
 * (blanca = 2 beats, redonda = 4, etc.) dentro de compases de ancho fijo.
 */
export function RhythmExerciseTimeline({
  expected,
  activeIndex,
  className = "",
  strumMode = false,
}: RhythmExerciseTimelineProps) {
  const measures = useMemo(() => buildMeasures(expected), [expected]);
  const activeRef = useRef<HTMLDivElement | null>(null);

  const activeMeasure = useMemo(() => {
    if (activeIndex < 0 || !expected[activeIndex]) return 0;
    return Math.floor(expected[activeIndex].beatOffset / 4 + 1e-9);
  }, [activeIndex, expected]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeMeasure, activeIndex]);

  if (!expected.length) return null;

  const measureWidth = 4 * TIMELINE_PX_PER_BEAT;

  return (
    <div
      className={`rounded-xl border border-white/10 bg-black/40 ${className}`}
      role="list"
      aria-label="Línea de tiempo del ejercicio"
    >
      <div className="overflow-x-auto px-2 pb-3 pt-2 sm:px-3">
        <div className="flex min-w-full gap-0">
          {measures.map((m) => {
            const isActive = m.index === activeMeasure;
            return (
              <div
                key={m.index}
                ref={isActive ? activeRef : undefined}
                role="listitem"
                className="relative shrink-0 border-r border-white/[0.08] last:border-r-0"
                style={{
                  width: measureWidth,
                  minWidth: measureWidth,
                  background: isActive
                    ? "rgba(153,246,228,0.06)"
                    : "transparent",
                  boxShadow: isActive
                    ? "inset 0 0 0 1px rgba(153,246,228,0.28)"
                    : "none",
                }}
              >
                {/* Guía de 4 beats */}
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 top-7 grid"
                  style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
                  aria-hidden
                >
                  {[0, 1, 2, 3].map((b) => (
                    <div
                      key={b}
                      className="border-l border-white/[0.04] first:border-l-0"
                    />
                  ))}
                </div>

                <p
                  className={`px-2 pt-1.5 text-[10px] font-medium tabular-nums ${
                    isActive ? "text-[#99f6e4]/80" : "text-white/35"
                  }`}
                >
                  {m.index + 1}
                </p>

                {/* Celdas proporcionales a durationBeats */}
                <div
                  className="relative grid items-end px-1 pb-2 pt-1"
                  style={{
                    gridTemplateColumns: m.cells
                      .map((c) => `${c.hit.durationBeats}fr`)
                      .join(" "),
                    minHeight: 88,
                  }}
                >
                  {m.cells.map(({ hit, globalIndex }) => {
                    const on = globalIndex === activeIndex;
                    const past = activeIndex >= 0 && globalIndex < activeIndex;
                    const color = hit.isRest
                      ? "rgba(251,191,36,0.35)"
                      : strumMode
                        ? "#99f6e4"
                        : STRING_BADGE_COLOR[hit.openString];

                    return (
                      <div
                        key={globalIndex}
                        className="relative flex min-w-0 flex-col items-center justify-end gap-1 px-0.5"
                        title={
                          hit.isRest || hit.gesture === "mute"
                            ? `Muteo · ${hit.durationBeats} tiempos`
                            : strumMode
                              ? `${hit.direction === "up" ? "Arriba ↑" : "Abajo ↓"} · ${hit.durationBeats} tiempos`
                              : `Cuerda ${hit.openString} · ${hit.durationBeats} tiempos`
                        }
                      >
                        <div
                          className="mb-0.5 h-1 w-full rounded-full transition-opacity"
                          style={{
                            background: on
                              ? color
                              : past
                                ? `${color}55`
                                : `${color}33`,
                            opacity: on ? 1 : 0.85,
                            boxShadow: on ? `0 0 10px ${color}88` : "none",
                          }}
                        />

                        {strumMode ? (
                          hit.isRest || hit.gesture === "mute" ? (
                            <span
                              className={`flex h-6 w-6 items-center justify-center text-sm font-bold ${
                                on ? "text-amber-200" : "text-amber-200/45"
                              }`}
                            >
                              ✕
                            </span>
                          ) : (
                            <span
                              className={`flex h-6 w-6 items-center justify-center text-lg font-bold leading-none ${
                                on ? "text-[#99f6e4]" : "text-white/40"
                              }`}
                            >
                              {hit.direction === "up" ? "↑" : "↓"}
                            </span>
                          )
                        ) : !hit.isRest ? (
                          <StringNumberBadge
                            openString={hit.openString}
                            active={on}
                            size="sm"
                          />
                        ) : (
                          <span className="flex h-6 w-6 items-center justify-center text-[9px] text-amber-200/50">
                            shh
                          </span>
                        )}

                        <div
                          className={`flex w-full items-center justify-center rounded-md py-1 transition-colors ${
                            on ? "bg-white/[0.06]" : ""
                          }`}
                        >
                          <RhythmFigureMini
                            durationBeats={hit.durationBeats}
                            isRest={hit.isRest}
                            dimmed={past && !on}
                            active={on}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
